import React, { useCallback, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';

import {
  DynamicInterviewMeetingHost,
  InterviewMeetingLocationInfo_InterviewMeetingLocationSettingPrefFragment,
  InterviewMeetingLocationModal_InterviewMeetingLocationSettingPrefFragment,
  InterviewMeetingLocationSettingPref,
  InterviewMeetingLocationSettingsInput,
  InterviewMeetingLocationType,
  MeetingLocationHostType,
  SettingLevel,
  VideoMeetingLinkScope,
  useTaskMeetingLocationQuery,
  useTaskUpdateMeetingLocationMutation,
} from 'src/generated/mloop-graphql';

import { SettingsRow } from 'src/components/SettingsRow/SettingsRow';

import InterviewMeetingLocationModal from 'src/entities/InterviewMeetingLocationModal';
import InterviewMeetingLocationInfo from 'src/entities/InterviewMeetingLocationModal/InterviewMeetingLocationInfo';

type Props = {
  taskId: string;
  disabled: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskMeetingLocationQueryGql = gql`
  ${InterviewMeetingLocationModal.fragments.interviewMeetingLocationSettingPref}
  ${InterviewMeetingLocationInfo.fragments.interviewMeetingLocationSettingPref}
  query TaskMeetingLocation($taskId: uuid!) {
    task(id: $taskId) {
      id
      meetingLocationSetting {
        kind: __typename
        ... on TaskMeetingLocationZoom {
          hostType
          hostEmployeeId
          remoteVideoMeetingHostUserId
        }
        ... on TaskMeetingLocationGoogleMeet {
          hostType
          hostEmployeeId
        }
        ... on TaskMeetingLocationMicrosoftTeams {
          hostType
          hostEmployeeId
        }
        ... on TaskMeetingLocationCustomLink {
          value
        }
        ... on TaskMeetingLocationCustomPhone {
          value
        }
        ... on TaskMeetingLocationCustomText {
          value
        }
      }
      jobStage {
        id
        jobStageSettingsV2 {
          interviewMeetingLocationSettingWithLevel {
            interviewMeetingLocationSetting {
              ...InterviewMeetingLocationModal_interviewMeetingLocationSettingPref
              ...InterviewMeetingLocationInfo_interviewMeetingLocationSettingPref
            }
          }
        }
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskUpdateMeetingLocationMutationGql = gql`
  mutation TaskUpdateMeetingLocation($input: TaskUpdateInput!) {
    taskUpdate(input: $input) {
      id
    }
  }
`;

type MeetingLocationSettingFragment = InterviewMeetingLocationModal_InterviewMeetingLocationSettingPrefFragment &
  InterviewMeetingLocationInfo_InterviewMeetingLocationSettingPrefFragment;

type TaskMeetingLocationSetting = {
  __typename?: string;
  kind?: string;
  hostType?: MeetingLocationHostType | null;
  hostEmployeeId?: string | null;
  remoteVideoMeetingHostUserId?: string | null;
  value?: string | null;
};

const EMPTY_SETTING = {
  interviewMeetingLocationType: InterviewMeetingLocationType.None,
  videoMeetingLinkScope: VideoMeetingLinkScope.VideoMeetingLinkScopeNotSet,
} as MeetingLocationSettingFragment;

const getDynamicHost = (hostType?: MeetingLocationHostType | null): DynamicInterviewMeetingHost | undefined => {
  if (hostType === MeetingLocationHostType.DynamicInterviewers) {
    return DynamicInterviewMeetingHost.Interviewers;
  }

  if (hostType === MeetingLocationHostType.DynamicScheduler) {
    return DynamicInterviewMeetingHost.Scheduler;
  }

  return undefined;
};

const getTaskOverrideSetting = (
  taskMeetingLocationSetting?: TaskMeetingLocationSetting | null
): MeetingLocationSettingFragment | undefined => {
  if (!taskMeetingLocationSetting) {
    return undefined;
  }

  const dynamicHost = getDynamicHost(taskMeetingLocationSetting.hostType);
  const hostEmployeeId =
    taskMeetingLocationSetting.hostType === MeetingLocationHostType.Employee
      ? taskMeetingLocationSetting.hostEmployeeId || undefined
      : undefined;
  const remoteVideoMeetingHostUserId =
    taskMeetingLocationSetting.hostType === MeetingLocationHostType.RemoteUser
      ? taskMeetingLocationSetting.remoteVideoMeetingHostUserId || undefined
      : undefined;

  // eslint-disable-next-line no-underscore-dangle
  const kind = taskMeetingLocationSetting.kind || taskMeetingLocationSetting.__typename;

  // Task overrides are stored as discriminated union types that don't carry a link scope; default to
  // NOT_SET so the object satisfies the shared meeting-location fragment shape.
  const videoMeetingLinkScope = VideoMeetingLinkScope.VideoMeetingLinkScopeNotSet;

  switch (kind) {
    case 'TaskMeetingLocationNone':
      return { videoMeetingLinkScope, interviewMeetingLocationType: InterviewMeetingLocationType.None };
    case 'TaskMeetingLocationCandidatePhone':
      return { videoMeetingLinkScope, interviewMeetingLocationType: InterviewMeetingLocationType.CandidatePhone };
    case 'TaskMeetingLocationCustomLink':
      return {
        videoMeetingLinkScope,
        interviewMeetingLocationType: InterviewMeetingLocationType.CustomLink,
        customLocation: taskMeetingLocationSetting.value || undefined,
      };
    case 'TaskMeetingLocationCustomPhone':
      return {
        videoMeetingLinkScope,
        interviewMeetingLocationType: InterviewMeetingLocationType.CustomPhone,
        customLocation: taskMeetingLocationSetting.value || undefined,
      };
    case 'TaskMeetingLocationCustomText':
      return {
        videoMeetingLinkScope,
        interviewMeetingLocationType: InterviewMeetingLocationType.CustomText,
        customLocation: taskMeetingLocationSetting.value || undefined,
      };
    case 'TaskMeetingLocationZoom':
      return {
        videoMeetingLinkScope,
        interviewMeetingLocationType: InterviewMeetingLocationType.Zoom,
        dynamicHost,
        hostEmployeeId,
        remoteVideoMeetingHostUserId,
      };
    case 'TaskMeetingLocationGoogleMeet':
      return {
        videoMeetingLinkScope,
        interviewMeetingLocationType: InterviewMeetingLocationType.GoogleMeet,
        dynamicHost,
        hostEmployeeId,
      };
    case 'TaskMeetingLocationMicrosoftTeams':
      return {
        videoMeetingLinkScope,
        interviewMeetingLocationType: InterviewMeetingLocationType.MicrosoftTeams,
        dynamicHost,
        hostEmployeeId,
      };
    default:
      return undefined;
  }
};

const toInterviewMeetingLocationSettingPref = (
  setting?: MeetingLocationSettingFragment
): InterviewMeetingLocationSettingPref => ({
  interviewMeetingLocationType: setting?.interviewMeetingLocationType || InterviewMeetingLocationType.None,
  dynamicHost: setting?.dynamicHost || undefined,
  hostEmployeeId: setting?.hostEmployee?.id || undefined,
  remoteVideoMeetingHostUserId: setting?.remoteVideoMeetingHostUser?.zoomUserId || undefined,
  customLocation: setting?.customLocation || undefined,
  videoMeetingLinkScope: setting?.videoMeetingLinkScope || VideoMeetingLinkScope.VideoMeetingLinkScopeNotSet,
});

const TaskMeetingLocation = ({ taskId, disabled }: Props) => {
  const { t } = useTranslation();
  const [showInterviewMeetingLocationModal, setShowInterviewMeetingLocationModal] = useState(false);

  const { data } = useTaskMeetingLocationQuery({
    variables: { taskId },
    fetchPolicy: 'cache-and-network',
  });

  const [taskUpdateMeetingLocationMutation] = useTaskUpdateMeetingLocationMutation();

  const inheritedSetting = data?.task?.jobStage?.jobStageSettingsV2?.interviewMeetingLocationSettingWithLevel
    ?.interviewMeetingLocationSetting as MeetingLocationSettingFragment | undefined;

  const taskOverrideSetting = useMemo(
    () => getTaskOverrideSetting(data?.task?.meetingLocationSetting as TaskMeetingLocationSetting | null | undefined),
    [data?.task?.meetingLocationSetting]
  );

  const effectiveSetting = taskOverrideSetting || inheritedSetting || EMPTY_SETTING;

  const handleChange = useCallback(
    async (
      _settingLevel: SettingLevel,
      interviewMeetingLocationSettingPref?: InterviewMeetingLocationSettingsInput
    ) => {
      await taskUpdateMeetingLocationMutation({
        variables: {
          input: {
            taskId,
            meetingLocationSetting: interviewMeetingLocationSettingPref,
          },
        },
      });
    },
    [taskId, taskUpdateMeetingLocationMutation]
  );

  const handleClearOverride = useCallback(async () => {
    await taskUpdateMeetingLocationMutation({
      variables: {
        input: {
          taskId,
          meetingLocationSetting: null,
        },
      },
    });
  }, [taskId, taskUpdateMeetingLocationMutation]);

  return (
    <>
      <Tooltip title={disabled ? t('hiring-dashboard-tasks:tasks.meetingLocation.disabledTooltip') : ''}>
        <Stack spacing={0.5}>
          <SettingsRow
            settingName={t('hiring-dashboard-common:common.location.title')}
            settingValue={<InterviewMeetingLocationInfo interviewMeetingLocationSettingPref={effectiveSetting} />}
            disabled={disabled}
            onClick={disabled ? undefined : () => setShowInterviewMeetingLocationModal(true)}
          />
          {taskOverrideSetting && (
            <Box px={1}>
              <Button size="small" disabled={disabled} onClick={handleClearOverride}>
                <Typography variant="caption">
                  {t('hiring-dashboard-tasks:tasks.meetingLocation.useDefault')}
                </Typography>
              </Button>
            </Box>
          )}
        </Stack>
      </Tooltip>

      {showInterviewMeetingLocationModal && (
        <InterviewMeetingLocationModal
          hideSyncWithOrgDefaultToggle
          settingLevel={SettingLevel.JobStage}
          orgInterviewMeetingLocationSetting={toInterviewMeetingLocationSettingPref(effectiveSetting)}
          interviewMeetingLocationSettingPref={effectiveSetting}
          onChange={handleChange}
          onClose={() => setShowInterviewMeetingLocationModal(false)}
        />
      )}
    </>
  );
};

export default TaskMeetingLocation;
